/**
 * /dashboard/search?q= (screenshots/114, §2.20): the query with its result count and time, tabs with counts,
 * results with an eyebrow, a linked title, the snippet with the hit highlighted and their facts, "Load more";
 * on the side "Follow “q”" and "Narrow results" (time, body, ward, only items I follow).
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { Building2, Check, Landmark, Plus, ScrollText, Search, Tag, UserRound } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { navigate } from "../app/router";
import { onTablistKey } from "../hooks/useTabs";
import { useFollows } from "../stores/followsStore";
import { NoResults } from "../components/shared/States";
import { List, ListRow, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { highlight, PAGE_TABS, pageText, searchUrl, type Scope, type SearchResponse, type SearchResult } from "./search";
import { followFromSearch, isSearchFollowed } from "./follow";

const PAGE = 20;
const TIMES = [["", "Any time"], ["week", "Past week"], ["month", "Past month"], ["year", "Past year"]] as const;
const WARDS = Array.from({ length: 50 }, (_, i) => String(i + 1));

function Icon({ r }: { r: SearchResult }) {
  const I = r.kind === "legislation" ? ScrollText : r.kind === "meeting" ? Landmark : r.kind === "person" ? UserRound : r.kind === "organization" ? Tag : r.kind === "filing" ? Tag : Building2;
  return <span className="sr-icon" data-kind={r.kind} aria-hidden><I size={13} /></span>;
}

/** The design's box ("Any time ▾") opening the shared Dropdown list. */
function Pick({ label, value, options, onChange }: { label: string; value: string; options: readonly (readonly [string, string])[]; onChange: (v: string) => void }) {
  const shown = options.find(([v]) => v === value)?.[1] ?? options[0][1];
  return (
    <Dropdown className="sr-pick" label={label} value={value} onChange={onChange} display={`${shown} ▾`} chevron={false}
      options={options.map(([v, t]) => ({ value: v, label: t }))} />
  );
}

export function SearchPage() {
  const follows = useFollows((s) => s.follows);
  const [q, setQ] = useState(() => new URLSearchParams(location.search).get("q") ?? "");
  const [tab, setTab] = useState<Scope>("all");
  const [time, setTime] = useState("");
  const [body, setBody] = useState("");
  const [ward, setWard] = useState("");
  const [mine, setMine] = useState(false);
  const [data, setData] = useState<SearchResponse | null>(null);
  const [more, setMore] = useState<SearchResult[]>([]);
  const [bodies, setBodies] = useState<string[]>([]);
  const [, bump] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const moreReq = useRef<AbortController | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const term = q.trim();

  useEffect(() => {
    moreReq.current?.abort(); // a page of the previous query must not land in this one
    setLoadingMore(false);
    setMore([]);
    if (term.length < 2) { setData(null); return; }
    const ctl = new AbortController();
    fetch(searchUrl(term, { kind: tab, scope: mine ? "follows" : "all", limit: PAGE, time, body, ward, deep: true, facets: true }), { credentials: "include", signal: ctl.signal })
      .then((r) => (r.ok ? r.json() : null)).then((d: SearchResponse | null) => { if (d) { setData(d); if (d.bodies) setBodies(d.bodies); } }).catch(() => {});
    return () => ctl.abort();
  }, [term, tab, time, body, ward, mine]);

  const loadMore = () => {
    if (!data || loadingMore) return; // one page at a time (a double click used to add the same page twice)
    const ctl = new AbortController();
    moreReq.current = ctl;
    setLoadingMore(true);
    fetch(searchUrl(term, { kind: tab, scope: mine ? "follows" : "all", limit: PAGE, offset: data.results.length + more.length, time, body, ward }), { credentials: "include", signal: ctl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: SearchResponse | null) => {
        if (!d) return;
        setMore((m) => { const seen = new Set([...data.results, ...m].map((x) => x.id)); return [...m, ...d.results.filter((x) => !seen.has(x.id))]; });
      })
      .catch(() => {})
      .finally(() => { if (moreReq.current === ctl) setLoadingMore(false); });
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = input.current?.value.trim() ?? "";
    navigate(`/dashboard/search?q=${encodeURIComponent(v)}`, { replace: true });
    setQ(v);
  };
  const results = data ? [...data.results, ...more] : [];
  const total = data?.total ?? 0;
  const keyword = { type: "topic" as const, keyword: term.toLowerCase(), label: term.charAt(0).toUpperCase() + term.slice(1) };
  const following = term.length > 1 && isSearchFollowed(keyword, follows);
  const hl = (t: string, r?: SearchResult) => highlight(t, term, r?.hl).map((p, i) => (p.hit ? <mark key={i} className="hit">{p.text}</mark> : <Fragment key={i}>{p.text}</Fragment>));

  return (
    <div className="search-page">
      <h1 className="page-title">Search</h1>
      <form role="search" className="sr-box" onSubmit={submit}>
        <Search size={20} aria-hidden />
        <input ref={input} name="q" aria-label="Search" defaultValue={q} autoComplete="off" />
        {data && <span className="sr-took">{data.total} results · {(data.tookMs / 1000).toFixed(2)} s</span>}
      </form>
      <div className="tabs sr-tabs" role="tablist" aria-label="Scope" onKeyDown={onTablistKey}>
        {PAGE_TABS.map((s) => (
          <button key={s.id} className="tab" role="tab" aria-selected={tab === s.id} tabIndex={tab === s.id ? 0 : -1} onClick={() => setTab(s.id)}>
            {s.label}{data && <span className="sr-count">{data.counts[s.id] ?? 0}</span>}
          </button>
        ))}
      </div>
      <div className="sr-body">
        <div className="sr-main">
          {data && !results.length && term.length > 1 && <NoResults query={term} />}
          {results.length > 0 && (
            <List as="ol" variant="plain" className="sr-list">
              {results.map((r) => {
                const t = pageText(r, r.personId ? data?.people[r.personId] : undefined);
                return (
                  <ListRow key={r.id} className="sr-item">
                    <RowMain>
                      <RowSub className="sr-label"><Icon r={r} />{t.label}</RowSub>
                      <RowTitle lines={3}>{r.href ? <a className="sr-title" href={r.href}>{t.title}</a> : <span className="sr-title">{t.title}</span>}</RowTitle>
                      {t.snippet && <p className="sr-snippet">{hl(t.snippet, r)}</p>}
                      {t.meta && <RowSub wrap className="sr-meta">{t.meta}</RowSub>}
                    </RowMain>
                  </ListRow>
                );
              })}
            </List>
          )}
          {results.length > 0 && (
            <p className="sr-more">
              Showing {results.length} of {total}
              {results.length < total && <> · <button type="button" onClick={loadMore} disabled={loadingMore} aria-busy={loadingMore || undefined}>Load more</button></>}
            </p>
          )}
        </div>
        <aside className="sr-side">
          {term.length > 1 && (
            <section className="sr-card">
              <h2>Follow “{term}”</h2>
              <p>Matches show up in Following and your digest.</p>
              {following
                ? <button className="btn btn--primary" disabled><Check size={14} aria-hidden />Following</button>
                : <button className="btn btn--primary" onClick={() => void followFromSearch(keyword).then(() => bump((n) => n + 1))}><Plus size={14} aria-hidden />Follow keyword</button>}
            </section>
          )}
          <section className="sr-card">
            <h2>Narrow results</h2>
            <Pick label="Time" value={time} options={TIMES} onChange={setTime} />
            <Pick label="Body" value={body} options={[["", "Any body"], ...bodies.map((b) => [b, b] as const)]} onChange={setBody} />
            <Pick label="Ward" value={ward} options={[["", "Any ward"], ...WARDS.map((w) => [w, `Ward ${w}`] as const)]} onChange={setWard} />
            <button type="button" className="sr-pick sr-toggle" aria-pressed={mine} onClick={() => setMine((v) => !v)}>Only items I follow</button>
          </section>
        </aside>
      </div>
    </div>
  );
}
